import React, { useMemo } from "react"
import parse from 'html-react-parser'
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { INLINES, BLOCKS, MARKS } from "@contentful/rich-text-types"
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image"
import { ContentfulRichTextType } from "../types/contentful.types"
import SmartLink from "../components/smart-link"
import { extendRichText } from "./rich-text-extends"
import Stores from "../components/stores"
import { stringToPath } from "./string-path-convertor"
import SlotsTypes from "../components/sections/slots-types"
import SlotsGallery from "../components/slots-gallery"
import VideoPlayer from "../components/video-player"
import useQueryParams from "query-params-hook"
import { replaceByDict } from "./replace-by-dict"

const options = (storesLinks) => ({
  renderMark: {
    [MARKS.BOLD]: (text) => <b className="font-bold">{text}</b>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => <code>{text}</code>,
  },
  renderText: (text) => {
    return text.split("\n").reduce((children, textSegment, index) => {
      const linedText: any = [
        ...children,
        index > 0 && <br key={index} />,
        textSegment,
      ]
      return extendRichText(linedText)
    }, [])
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      return <SmartLink url={node.data.uri}>{children}</SmartLink>
    },
    [INLINES.EMBEDDED_ENTRY]: (node, children) => {
      const type = node.data.target.__typename
      const target = node.data.target;
      switch (type) {
        case "ContentfulComponentImageLink":
          const imageUrl = target.image.localFile.publicURL
          const alt = target.title
          const url = target.linkToInnerPage
            ? target.linkToInnerPage.slug
            : target.linkToUrl
          return (
            <SmartLink
              url={url}
              target={target.openInNewWindow ? "_blank" : "_self"}
            >
              <img src={imageUrl} alt={alt} />
            </SmartLink>
          )
        default:
          console.log(`${type} does not exists on richtext`)
          return null
      }
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // QUOTES ONLY VISIBLE IN BLOG VIA CSS blog.css
      return <p className="richtext-p">{children}</p>
    },
    [BLOCKS.HEADING_1]: (node, children) => {
      const values = typeof children[0] === "object" && !Array.isArray(children[0]) ? children: children[0][1].split("[br]");
      return <h1 className="uppercase font-extrabold">{values.map?.((value) => <>{value}<br /></>) || values}</h1>
    },
    [BLOCKS.HEADING_2]: (node, children) => {
      let id = ""
      if (typeof children[0][1] == "string") {
        id = children[0][1]
      } else if (
        children[0]["props"] &&
        typeof children[0]["props"]["children"][1] == "string"
      ) {
        id = children[0]["props"]["children"][1]
      }

      return (
        <h2
          id={stringToPath(id, true).replace(/[\W\d]/g, "")}
          className="font-extrabold text-[22px] xl:text-[54px] pt-10 xl:pt-28 uppercase leading-9 md:leading-[5rem]"
        >
          {children}
        </h2>
      )
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      return (
        <h3 className="text-[20px] xl:text-[35px] xl:leading-normal font-bold">
          {children}
        </h3>
      )
    },
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
    [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
    [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
    [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      switch (node.data.target.__typename) {
        default:
          console.log(node.data.target.__typename)
          break

        case "ContentfulAsset":
          return (
            <>
              <GatsbyImage
                image={
                  getImage(
                    node.data.target.localFile.childImageSharp.gatsbyImageData
                  )!
                }
                alt=''
              />
              <div className='blog-image-description'>
                {node.data.target.description}
              </div>
            </>
          )
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const type = node.data.target.__typename
      const target = node.data.target
      switch (type) {
        case "ContentfulComponentYoutubeVideo":
          return (
            <div className="video-container">
              <VideoPlayer
                youtube={true}
                url={target.youtubeUrl.youtubeUrl}
              />
            </div>
          )
        case "ContentfulComponentHtmlCode":
          return (
            <div className="html-container">
              {parse(target.html.html)}
            </div>
          )
        case "ContentfulComponentStores":
          const stores = target.storesList ? target.storesList.reduce((acc, { name, mobile, desktop }) => ([ ...acc, {name, mobile: mobile?.mobile, desktop: desktop.desktop} ]), []) : undefined;
          return <Stores links={storesLinks ?? stores} />
        case "ContentfulComponentSlotsTypes":
          return (
            <SlotsGallery
              data={target.slots}
              containerClass="overflow-hidden"
              slidesToShow={6}
              showSectionTitle={false}
              showReflection={false}
              mobileTwoRows={false}
              showSeeAll={false}
              onPlayNowClick={function (slot: any) {
                throw new Error("Function not implemented.")
              }}
            />
          )
        default:
          console.log(`${type} does not exists on BLOCKS richtext`)
          return null
      }
    },
    [BLOCKS.QUOTE]: (node, children) => {
      return (
        <div className="blog-quotes my-10">
          <StaticImage
            className="quote mr-3"
            src="../images/blog/quote-open.png"
            alt="Quote Open"
            width={24}
          />
          {node.content[0].content[0].value}
          <StaticImage
            className="quote ml-3"
            src="../images/blog/quote-close.png"
            alt="Quote Open"
            width={24}
          />
        </div>
      )
    },
    [BLOCKS.TABLE]: (node, children) => {
      return (
        <div className="table-container">
          <table>{children}</table>
        </div>
      )
    },
  },
})

const ContentfulRichText = ({
  raw,
  className,
  style,
}: ContentfulRichTextType) => {
  const { lp: lpPages, campaignid, adgroupid, keyword, adid, lpurl, gclid} = useQueryParams()
  const isLpPage = lpPages === "true";
  const replaceHashed = useMemo(
    () => ({ campaignid, adgroupid, keyword, adid, lpurl, gclid }),
    [campaignid, adgroupid, keyword, adid, lpurl, gclid]
  );

  const storesLinks = useMemo(() => {
    return (isLpPage ? [
      {name: "Google", desktop: replaceByDict("https://app.adjust.com/xmapw1z?campaign={campaignid}&adgroup={adgroupid}&creative={keyword}_{adid}&lpurl={lpurl}&external_click_id={gclid}", replaceHashed)},
      {name: "Apple", desktop: replaceByDict("https://app.adjust.com/nkxn20i?campaign={campaignid}&adgroup={adgroupid}&creative={keyword}_{adid}&lpurl={lpurl}&external_click_id={gclid}&redirect=https://apps.apple.com/app/id945621521?ppid=f515ce68-eb0a-4115-8c92-e4ade2811b09", replaceHashed)},
    ] : undefined)
  }, [isLpPage, replaceHashed]);

  return (
    <div className={className} style={style}>
      {renderRichText(
        raw,
        options(storesLinks)
      )}
    </div>
  )
}

export default ContentfulRichText